import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { pprwAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Plus, Search, Loader2, Layers, ChevronRight, Trash2, Pencil, LayoutTemplate } from 'lucide-react';
import { WorkshopFormDialog } from './WorkshopFormDialog';
import { WorkshopTemplateDialog } from './WorkshopTemplateDialog';
import { AddSessionsDialog } from './AddSessionsDialog';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const STATUS_SEG = {
  Scheduled: '#3b82f6', Upcoming: '#a855f7', 'In Progress': '#f59e0b',
  Completed: '#10b981', 'On Hold': '#f97316', Cancelled: '#ef4444',
};

const RollupBar = ({ counts = {}, total = 0 }) => {
  if (!total) return <span className="text-xs text-muted-foreground">No sessions</span>;
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-2 w-28 rounded-full overflow-hidden bg-muted">
        {Object.entries(counts).map(([k, v]) => (
          <div key={k} style={{ width: `${(v / total) * 100}%`, background: STATUS_SEG[k] || '#94a3b8' }} title={`${k}: ${v}`} />
        ))}
      </div>
      <span className="text-xs text-muted-foreground">{total}</span>
    </div>
  );
};

const ReadinessPill = ({ readiness, testId }) => {
  if (!readiness || (!readiness.content && !readiness.lab)) return null;
  const atRisk = readiness.at_risk;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={testId}>
      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${atRisk ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
        {atRisk ? 'At risk' : 'On track'}
      </span>
      <span className="text-[10px] text-muted-foreground">Content: {readiness.content || '—'} · Lab: {readiness.lab || '—'}</span>
    </div>
  );
};

export function WorkshopsTab({ categories = [] }) {
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [addTarget, setAddTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setWorkshops(await pprwAPI.workshops.getAll()); }
    catch (_e) { toast.error('Failed to load workshops'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = search
    ? workshops.filter(w => (w.title || '').toLowerCase().includes(search.toLowerCase()) ||
        (w.solution_area || '').toLowerCase().includes(search.toLowerCase()) ||
        (w.ms_owner || '').toLowerCase().includes(search.toLowerCase()))
    : workshops;

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this workshop? Its sessions will be kept but detached.')) return;
    setDeleting(id);
    try { await pprwAPI.workshops.delete(id); toast.success('Workshop deleted'); load(); }
    catch { toast.error('Delete failed'); }
    finally { setDeleting(null); }
  };

  return (
    <div data-testid="pprw-workshops-tab">
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="relative flex-1 min-w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search workshops by course, solution area, MS Owner…" className="pl-9" data-testid="workshops-search-input" />
        </div>
        <Button size="sm" variant="outline" className="gap-2 ml-auto" onClick={() => navigate('/pprw/workshop-templates')} data-testid="manage-templates-btn">
          <LayoutTemplate className="w-4 h-4" />Manage Templates
        </Button>
        <Button size="sm" variant="outline" className="gap-2" onClick={() => setShowTemplate(true)} data-testid="workshop-from-template-btn">
          <LayoutTemplate className="w-4 h-4" />From Template
        </Button>
        <Button size="sm" className="gap-2" onClick={() => { setEditTarget(null); setShowForm(true); }} data-testid="workshop-create-btn">
          <Plus className="w-4 h-4" />New Workshop
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No workshops yet.</p>
          <p className="text-xs mt-1">Create one here, or multi-select sessions on the Sessions tab and “Create Workshop from selection”.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden" data-testid="workshops-list">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                {['Workshop', 'MS Owner', 'PMO', 'Dates', 'Readiness', 'Sessions', ''].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(w => (
                <tr key={w.id} className="border-t border-border hover:bg-muted/30 cursor-pointer group" onClick={() => navigate(`/pprw/workshops/${w.id}`)} data-testid={`workshop-row-${w.id}`}>
                  <td className="px-3 py-3 max-w-[280px]">
                    <p className="font-semibold truncate">{w.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{w.solution_area || '—'}{w.delivery_platform ? ` · ${w.delivery_platform}` : ''}</p>
                  </td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{w.ms_owner || '—'}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{w.pmo || '—'}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{fmtDate(w.start_date)} → {fmtDate(w.end_date)}</td>
                  <td className="px-3 py-3">
                    {w.readiness && (w.readiness.content || w.readiness.lab) ? (
                      w.readiness.at_risk ? (
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/pprw/workshops/${w.id}?filter=blocking`); }} title="View blocking sessions" data-testid={`workshop-readiness-${w.id}`}>
                          <ReadinessPill readiness={w.readiness} />
                        </button>
                      ) : <ReadinessPill readiness={w.readiness} testId={`workshop-readiness-${w.id}`} />
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-3"><RollupBar counts={w.status_counts} total={w.session_count} /></td>
                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="p-1.5 rounded hover:bg-muted" title="Add sessions" onClick={() => setAddTarget(w)} data-testid={`workshop-add-sessions-btn-${w.id}`}>
                        <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                      <button className="p-1.5 rounded hover:bg-muted" title="Edit" onClick={() => { setEditTarget(w); setShowForm(true); }} data-testid={`workshop-edit-btn-${w.id}`}>
                        <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                      <button className="p-1.5 rounded hover:bg-red-50" title="Delete" onClick={() => handleDelete(w.id)} disabled={deleting === w.id} data-testid={`workshop-delete-btn-${w.id}`}>
                        {deleting === w.id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-red-400" /> : <Trash2 className="w-3.5 h-3.5 text-red-400" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <WorkshopFormDialog open={showForm} onClose={() => { setShowForm(false); setEditTarget(null); }}
          onSaved={load} existing={editTarget} categories={categories} />
      )}
      {showTemplate && (
        <WorkshopTemplateDialog open={showTemplate} onClose={() => setShowTemplate(false)} onCreated={(wid) => navigate(`/pprw/workshops/${wid}`)} />
      )}
      {addTarget && (
        <AddSessionsDialog open={!!addTarget} onClose={() => setAddTarget(null)} workshopId={addTarget.id} onAdded={load} />
      )}
    </div>
  );
}

export default WorkshopsTab;
